import { X } from 'lucide-react';
import { useId, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

import { IconButton } from './IconButton';
import { cx } from './shared';
import { useDialogFocus } from './useDialogFocus';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  ariaLabel?: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
  closeOnBackdrop?: boolean;
  maxWidth?: string;
  initialFocusRef?: RefObject<HTMLElement | null>;
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
};

export default function Modal({
  isOpen,
  onClose,
  title,
  ariaLabel,
  description,
  children,
  footer,
  size = 'md',
  showCloseButton = true,
  closeOnBackdrop = true,
  maxWidth,
  initialFocusRef,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const titleId = title ? `modal-title-${generatedId}` : undefined;
  const descriptionId = description ? `modal-description-${generatedId}` : undefined;

  useDialogFocus(isOpen, onClose, dialogRef, initialFocusRef);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-scrim p-page-inline-compact sm:p-page-inline-sm"
      onMouseDown={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-label={title ? undefined : (ariaLabel ?? 'Dialog')}
        aria-describedby={descriptionId}
        tabIndex={-1}
        className={cx(
          'so-overlay-enter flex max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden rounded-overlay border border-border-strong bg-surface-raised text-ink shadow-blocking',
          maxWidth ?? sizeClasses[size],
        )}
      >
        {title || showCloseButton ? (
          <header className="flex shrink-0 items-start justify-between gap-field-gap border-b border-border-subtle px-6 py-4">
            <div className="min-w-0">
              {title ? (
                <h2 id={titleId} className="text-section-title font-semibold text-ink-strong">
                  {title}
                </h2>
              ) : null}
              {description ? (
                <div id={descriptionId} className="mt-1 max-w-reading text-body text-ink-muted">
                  {description}
                </div>
              ) : null}
            </div>
            {showCloseButton ? (
              <IconButton label="Close dialog" onClick={onClose} className="-mr-2 -mt-2">
                <X className="h-5 w-5" />
              </IconButton>
            ) : null}
          </header>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer ? (
          <footer className="flex shrink-0 flex-wrap justify-end gap-inline-control-gap border-t border-border-subtle px-6 py-4">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
